import { lazy } from 'react';

// project imports
import Loadable from 'components/Loadable';
import DashboardLayout from 'layout/Dashboard';
import ProtectedRoute from 'components/ProtectedRoute';

// Admin Dashboard
const AdminDashboard = Loadable(lazy(() => import('pages/admin/dashboard')));

// 회원관리
const MemberList = Loadable(lazy(() => import('pages/admin/members/MemberList')));
const MemberForm = Loadable(lazy(() => import('pages/admin/members/MemberForm')));
const WithdrawnMembers = Loadable(lazy(() => import('pages/admin/members/WithdrawnMembers')));
const MemberGrade = Loadable(lazy(() => import('pages/admin/members/MemberGrade')));
const ManagerList = Loadable(lazy(() => import('pages/admin/members/ManagerList')));

// 예약관리
const ConsultationList = Loadable(lazy(() => import('pages/admin/reservations/ConsultationList')));

// 게시판관리
const NoticeList = Loadable(lazy(() => import('pages/admin/boards/NoticeList')));
const NoticeForm = Loadable(lazy(() => import('pages/admin/boards/NoticeForm')));
const NoticeDetail = Loadable(lazy(() => import('pages/admin/boards/NoticeDetail')));
const FaqList = Loadable(lazy(() => import('pages/admin/boards/FaqList')));
const FaqForm = Loadable(lazy(() => import('pages/admin/boards/FaqForm')));
const CustomerVoiceList = Loadable(lazy(() => import('pages/admin/boards/CustomerVoiceList')));
const BoardList = Loadable(lazy(() => import('pages/admin/boards/BoardList')));
const BoardForm = Loadable(lazy(() => import('pages/admin/boards/BoardForm')));
const TaxExemptList = Loadable(lazy(() => import('pages/admin/boards/TaxExemptList')));
const TaxExemptForm = Loadable(lazy(() => import('pages/admin/boards/TaxExemptForm')));
const TaxExemptDetail = Loadable(lazy(() => import('pages/admin/boards/TaxExemptDetail')));

// 콘텐츠관리
const BannerList = Loadable(lazy(() => import('pages/admin/contents/BannerList')));
const BannerForm = Loadable(lazy(() => import('pages/admin/contents/BannerForm')));
const PopupList = Loadable(lazy(() => import('pages/admin/contents/PopupList')));
const PopupForm = Loadable(lazy(() => import('pages/admin/contents/PopupForm')));

// 의료진관리
const DoctorList = Loadable(lazy(() => import('pages/admin/medical/DoctorList')));
const DoctorForm = Loadable(lazy(() => import('pages/admin/medical/DoctorForm')));
const ScheduleList = Loadable(lazy(() => import('pages/admin/medical/ScheduleList')));

// 진료센터관리
const CenterList = Loadable(lazy(() => import('pages/admin/centers/CenterList')));
const CenterForm = Loadable(lazy(() => import('pages/admin/centers/CenterForm')));

// 환자후기관리
const PatientReviewList = Loadable(lazy(() => import('pages/admin/reviews/PatientReviewList')));
const PatientReviewForm = Loadable(lazy(() => import('pages/admin/reviews/PatientReviewForm')));

// 사회공헌관리
const SocialContributionList = Loadable(lazy(() => import('pages/admin/contributions/SocialContributionList')));
const SocialContributionForm = Loadable(lazy(() => import('pages/admin/contributions/SocialContributionForm')));

// 통계
const VisitorStats = Loadable(lazy(() => import('pages/admin/statistics/VisitorStats')));
const ReservationStats = Loadable(lazy(() => import('pages/admin/statistics/ReservationStats')));
const TrafficStats = Loadable(lazy(() => import('pages/admin/statistics/TrafficStats')));

// 환경설정
const AdminAccount = Loadable(lazy(() => import('pages/admin/settings/AdminAccount')));
const SeoSettings = Loadable(lazy(() => import('pages/admin/settings/SeoSettings')));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: '/',
  element: (
    <ProtectedRoute>
      <DashboardLayout />
    </ProtectedRoute>
  ),
  children: [
    // 대시보드
    {
      path: 'dashboard',
      element: <AdminDashboard />
    },

    // 회원관리
    {
      path: 'members',
      children: [
        { path: 'list', element: <MemberList /> },
        { path: ':id/edit', element: <MemberForm /> },
        { path: 'withdrawn', element: <WithdrawnMembers /> },
        { path: 'grade', element: <MemberGrade /> }
      ]
    },

    // 관리자관리
    {
      path: 'managers',
      children: [
        { path: 'list', element: <ManagerList /> }
      ]
    },

    // 예약관리
    {
      path: 'reservations',
      children: [
        { path: 'consultations', element: <ConsultationList /> },
        { path: 'manage', element: <ConsultationList /> }
      ]
    },

    // 게시판관리
    {
      path: 'boards',
      children: [
        // 공지사항
        { path: 'notices', element: <NoticeList /> },
        { path: 'notices/create', element: <NoticeForm /> },
        { path: 'notices/:id', element: <NoticeDetail /> },
        { path: 'notices/:id/edit', element: <NoticeForm /> },
        // FAQ
        { path: 'faqs', element: <FaqList /> },
        { path: 'faqs/create', element: <FaqForm /> },
        { path: 'faqs/:id', element: <FaqForm /> },
        { path: 'faqs/:id/edit', element: <FaqForm /> },
        // 고객의 소리
        { path: 'customer-voices', element: <CustomerVoiceList /> },
        // 통합 게시판
        { path: 'posts', element: <BoardList /> },
        { path: 'posts/create', element: <BoardForm /> },
        { path: 'posts/:id', element: <BoardForm /> },
        { path: 'posts/:id/edit', element: <BoardForm /> },
        // 비급여안내
        { path: 'tax-exempt', element: <TaxExemptList /> },
        { path: 'tax-exempt/create', element: <TaxExemptForm /> },
        { path: 'tax-exempt/:type/detail', element: <TaxExemptDetail /> }
      ]
    },

    // 콘텐츠관리
    {
      path: 'contents',
      children: [
        // 배너
        { path: 'banners', element: <BannerList /> },
        { path: 'banners/create', element: <BannerForm /> },
        { path: 'banners/:id', element: <BannerForm /> },
        { path: 'banners/:id/edit', element: <BannerForm /> },
        // 팝업
        { path: 'popups', element: <PopupList /> },
        { path: 'popups/create', element: <PopupForm /> },
        { path: 'popups/:id', element: <PopupForm /> },
        { path: 'popups/:id/edit', element: <PopupForm /> },
        // 섹션
        { path: 'sections', element: <BannerList /> }
      ]
    },

    // 의료진관리
    {
      path: 'medical',
      children: [
        { path: 'doctors', element: <DoctorList /> },
        { path: 'doctors/create', element: <DoctorForm /> },
        { path: 'doctors/:id', element: <DoctorForm /> },
        { path: 'doctors/:id/edit', element: <DoctorForm /> },
        { path: 'schedules', element: <ScheduleList /> }
      ]
    },

    // 진료센터관리
    {
      path: 'centers',
      children: [
        { index: true, element: <CenterList /> },
        { path: 'create', element: <CenterForm /> },
        { path: ':id', element: <CenterForm /> },
        { path: ':id/edit', element: <CenterForm /> }
      ]
    },

    // 환자후기관리
    {
      path: 'reviews',
      children: [
        { index: true, element: <PatientReviewList /> },
        { path: 'create', element: <PatientReviewForm /> },
        { path: ':id', element: <PatientReviewForm /> },
        { path: ':id/edit', element: <PatientReviewForm /> }
      ]
    },

    // 사회공헌관리
    {
      path: 'contributions',
      children: [
        { index: true, element: <SocialContributionList /> },
        { path: 'create', element: <SocialContributionForm /> },
        { path: ':id', element: <SocialContributionForm /> },
        { path: ':id/edit', element: <SocialContributionForm /> }
      ]
    },

    // 통계
    {
      path: 'statistics',
      children: [
        { path: 'visitors', element: <VisitorStats /> },
        { path: 'reservations', element: <ReservationStats /> },
        { path: 'traffic', element: <TrafficStats /> }
      ]
    },

    // 환경설정
    {
      path: 'settings',
      children: [
        { path: 'admins', element: <AdminAccount /> },
        { path: 'seo', element: <SeoSettings /> }
      ]
    }
  ]
};

export default MainRoutes;
